import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Select } from "@/components/design/Select";
import { MatchTeamProfilesDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { PowerProfileEditor } from "./PowerProfileEditor";
import { SubsectionHeader } from "./requirements/row-primitives";
import type { MatchData } from "./match-design-types";
import { BALANCED_TEAM_PROFILE_SEED_ID, DEFAULT_TEAM_PROFILE_ID, LEGACY_BALANCED_TEAM_PROFILE_ID, POWERS_WEIGHT_MIN, densifyElementWeights, makeIndividualTeamProfile, normalizePowersWeight } from "./match-design-rules";

export function PowersPicker({
  profile,
  onChange,
  readOnly,
  id = "match-team-profile",
  editorIdPrefix,
}: {
  profile: MatchData["teamProfile"];
  onChange: (profile: MatchData["teamProfile"]) => void;
  readOnly: boolean;
  id?: string;
  editorIdPrefix?: string;
}) {
  const intl = useIntl();
  const { data, loading, error } = useQuery(MatchTeamProfilesDocument, {
    variables: { input: { first: 50 } },
  });
  const profiles = useMemo(() => {
    return (data?.teamProfiles?.nodes ?? []).map((p) => {
      const enumWeights: Record<string, number> = {};
      for (const { element, weight } of p.weights ?? []) {
        if (element) {
          enumWeights[element] = normalizePowersWeight(weight);
        }
      }
      return {
        id: p.id,
        name: p.name ?? p.id,
        label:
          p.localizedName?.localized ||
          p.name ||
          p.id,
        // Densify at the picker seam so downstream consumers (preset apply,
        // Individuell switch, reset, divergence check) never see a sparse
        // preset. Missing keys default to 0 (preset author intent).
        // Without this, switching to Individuell would carry the sparse map
        // forward, then dataToDesign would fill the gaps with 5 — inflating
        // previously-excluded Powers and silently changing the brief.
        elementWeights: densifyElementWeights(enumWeights, POWERS_WEIGHT_MIN),
      };
    });
  }, [data]);

  // Designs saved before genesis#211 reference the old client-side synthetic
  // "Ausgewogen"; the seeded row is the same profile, so map the id onto it and
  // the dropdown keeps showing a selection for those matches.
  const savedProfileId = profile?.id ?? DEFAULT_TEAM_PROFILE_ID;
  const selectedProfileId =
    savedProfileId === LEGACY_BALANCED_TEAM_PROFILE_ID
      ? BALANCED_TEAM_PROFILE_SEED_ID
      : savedProfileId;

  // A design can reference a profile the list no longer carries: admins can now
  // delete Power Profiles (spark#218), and the list is still loading on first
  // paint. Carry the stored one as its own option under the name saved in the
  // design, otherwise the Select falls back to rendering the first option and
  // the brief silently reads "Individuell" while still holding those weights.
  const pickerProfiles = useMemo(() => {
    if (!profile || selectedProfileId === DEFAULT_TEAM_PROFILE_ID) return profiles;
    if (profiles.some((p) => p.id === selectedProfileId)) return profiles;
    const label = profile.name || selectedProfileId;
    return [...profiles, { id: selectedProfileId, name: profile.name, label, elementWeights: profile.elementWeights }];
  }, [profiles, profile, selectedProfileId]);

  // Divergence: is the live profile's Importance shape identical to the source
  // preset's saved shape? If not, the dropdown label gets a "· geändert"
  // suffix so the user knows they've drifted. Reselecting the preset replaces
  // the weights — the dropdown is its own reset path. See @adr SPARK-0033-power-weights-word-scale-plus-exact-field@1.
  const sourcePreset = profile
    ? pickerProfiles.find((p) => p.id === selectedProfileId)
    : null;
  const isCustom = profile?.id === DEFAULT_TEAM_PROFILE_ID;
  const isModified = useMemo(() => {
    if (!profile || !sourcePreset) return false;
    const a = profile.elementWeights;
    const b = sourcePreset.elementWeights as Record<string, number>;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      if ((a[k] ?? 0) !== (b[k] ?? 0)) return true;
    }
    return false;
  }, [profile, sourcePreset]);

  function setProfile(id: string) {
    if (id === DEFAULT_TEAM_PROFILE_ID) {
      // Switching to Individuell keeps the current weights – picking it is the
      // user saying "I'm no longer following a preset", not "reset my shape".
      // The Zurücksetzen button is the explicit reset path.
      onChange(makeIndividualTeamProfile(profile?.elementWeights));
      return;
    }
    const sel = pickerProfiles.find((p) => p.id === id);
    if (!sel) return;
    onChange({
      id: sel.id,
      name: sel.name,
      elementWeights: { ...sel.elementWeights },
    });
  }

  function resetToPreset() {
    if (!profile || !sourcePreset) return;
    onChange({
      id: sourcePreset.id,
      name: sourcePreset.name,
      elementWeights: { ...(sourcePreset.elementWeights as Record<string, number>) },
    });
  }

  const canReset = !readOnly && isModified && !isCustom && Boolean(sourcePreset);

  const modifiedSuffix = intl.formatMessage({
    id: "match.teamProfileModifiedSuffix",
    defaultMessage: " · geändert",
  });
  const customLabel = intl.formatMessage({
    id: "match.teamProfileCustom",
    defaultMessage: "Individuell",
  });

  return (
    <div className="space-y-5">
      <div>
        <SubsectionHeader
          title={intl.formatMessage({ id: "match.teamProfile", defaultMessage: "Selection" })}
        />
        <Select
          id={id}
          value={selectedProfileId}
          onChange={(next) => setProfile(next)}
          disabled={loading || readOnly}
          hideEmpty
          options={[
            { value: DEFAULT_TEAM_PROFILE_ID, label: customLabel },
            ...pickerProfiles.map((p) => ({
              value: p.id,
              label: isModified && selectedProfileId === p.id ? `${p.label}${modifiedSuffix}` : p.label,
            })),
          ]}
        />
        {error && (
          <div className="mt-1 text-smycles-label text-red-600">
            <FormattedMessage id="match.profilesLoadError" defaultMessage="Profile laden: {message}" values={{ message: userErrorMessage(intl, error) }} />
          </div>
        )}
      </div>

      {profile ? <PowerProfileEditor profile={profile} onChange={onChange} readOnly={readOnly} idPrefix={editorIdPrefix} /> : null}

      {canReset ? (
        <div className="flex items-center justify-end">
          <button
            onClick={resetToPreset}
            className="text-smycles-label tracking-widest text-ink-muted underline-offset-2 hover:text-ink hover:underline"
          >
            <FormattedMessage id="match.editor.reset" defaultMessage="Zurücksetzen" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
